/**
 * tienda.js — Render de los bloques de tienda: `ofertas`, `novedades`, `top` (más vendidos) y
 * `habitual` (lo que sueles comprar). Handoff de diseño del 06-10-2026 («Unificación de bloques
 * tienda»); plan y contratos en CLAUDE_RESUMEN_PLANES/2026-10-06_tienda-bloques/PLAN.md.
 *
 * El render NO lleva datos: pinta la sección con sus huecos (`data-ts-slot`), unas tarjetas de
 * relleno y, en `data-tienda`, todo lo que el runtime (core/tienda-runtime.js) necesita para pedir
 * los datos y pintarlos: tipo, cantidad, orden, quién la ve, precio, botón, la URL de la tienda y
 * las etiquetas YA TRADUCIDAS al idioma de la página (el HTML publicado no carga
 * `NUBE3I_UI_LABELS`: lo que pinte el runtime tiene que llevarlas horneadas).
 *
 * Estados (`data-ts-estado`, lo cambia el runtime; el CSS enseña lo que toca):
 *   cargando · items · vacio · gate (aviso para entrar) · oculto · off (tienda o lista apagada)
 * Nace en `cargando` si la ve todo el mundo y en `pendiente` si pide sesión: así una sección
 * «solo clientes» no asoma a nadie hasta que el runtime sabe quién mira.
 *
 * Todo lo del editor (barra de opciones, marcadores, nota de muestra) va `.editor-only`, y el
 * título y los textos del aviso son `data-field` editables (traducibles).
 */
import { safe, hexToRgb, getLuminance } from '../utils/contrast.js';
import { t, getRenderLang } from '../utils/i18n.js';
import { escapeHtml } from '../utils/dom.js';
import { getIcon } from '../utils/icons-library.js';
import { attrJson } from './reserva-live.js';
import { TIENDA_TIPOS, opcionesTienda, esqueletos, getTiendaShop } from './tienda-comun.js';

// Textos del EDITOR (no viajan a lo publicado: van en chrome `.editor-only`).
const MARCADOR_VACIO = {
    ofertas: 'Este cliente no tiene ofertas destacadas ahora mismo, así que la sección no se muestra.',
    novedades: 'Ahora mismo no hay novedades que mostrar, así que la sección no se muestra.',
    top: 'Ahora mismo no hay productos que mostrar, así que la sección no se muestra.',
    habitual: 'Este cliente no tiene compras todavía, así que la sección no se muestra.',
};
const SOLO_EDITOR = ' Este marcador solo existe en el editor.';

const _plano = (html) => String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

// Las etiquetas que el runtime pinta en las tarjetas, en el idioma de render.
function etiquetas() {
    return {
        add: t('shop_add'), view: t('shop_view'), see_products: t('shop_see_products'),
        valid_until: t('shop_valid_until'), until: t('shop_until'), upto: t('shop_upto'),
        gift: t('shop_gift'), with_gift: t('shop_with_gift'),
    };
}

// Superficie de las tarjetas: blanco sobre un fondo claro (el diseño) y, sobre uno oscuro, un
// punto más clara que la sección para que el texto del tema siga leyéndose.
function superficie(bg) {
    try {
        return getLuminance(...hexToRgb(bg)) < 0.3
            ? 'color-mix(in srgb, var(--bg) 90%, #ffffff)' : '#ffffff';
    } catch (e) {
        return '#ffffff';
    }
}

function grupo(label, icono, botones) {
    return '<div class="ts-tb-group" role="group" aria-label="' + escapeHtml(label) + '">' +
        '<span class="ts-tb-label">' + (icono ? getIcon(icono, 13) : '') + escapeHtml(label) + '</span>' + botones + '</div>';
}

function boton(campo, valor, texto, activo, deshabilitado) {
    return '<button type="button" class="ts-tb-btn' + (activo ? ' is-active' : '') + (deshabilitado ? ' is-disabled' : '') + '"' +
        ' data-ts-set="' + campo + '" data-ts-val="' + valor + '"' +
        (deshabilitado ? ' aria-disabled="true" title="' + escapeHtml(deshabilitado) + '"' : '') + '>' + escapeHtml(texto) + '</button>';
}

// Barra del bloque (solo editor). «Ver como» no es un prop: lo guarda el runtime (vista previa).
function barra(kind, o) {
    const cfg = TIENDA_TIPOS[kind];
    const variantes = { v1: 'Cuadrícula', v2: 'Carrusel', v3: 'Banner' };
    let html = grupo('La ve', 'lock',
        boton('visibility', 'todos', 'Todo el mundo', o.visibility === 'todos',
              cfg.soloSesion ? 'Necesita el histórico de compras: solo con sesión' : '') +
        boton('visibility', 'sesion', 'Solo con sesión', o.visibility === 'sesion'));
    if (o.visibility === 'sesion') {
        html += grupo('Sin sesión', '',
            boton('fallback', 'ocultar', 'Ocultar', o.fallback === 'ocultar') +
            boton('fallback', 'aviso', 'Aviso', o.fallback === 'aviso'));
    }
    html += grupo('Ver como', 'eye',
        boton('ver', 'con', 'Con sesión', true) + boton('ver', 'sin', 'Sin sesión', false) +
        boton('ver', 'vacio', 'Sin contenido', false));
    if (cfg.producto) {
        html += grupo('Precio', '',
            boton('prices', 'show', 'Con precio', o.prices === 'show') +
            boton('prices', 'hide', 'Sin precio', o.prices === 'hide'));
        html += grupo('Botón', 'shopping-cart',
            boton('cart', 'ver', 'Ver', o.cart === 'ver') +
            boton('cart', 'cesta', 'Añadir a la cesta', o.cart === 'cesta'));
    }
    html += grupo('Diseño', '', cfg.variants.map((v) => boton('variant', v, variantes[v], o.variant === v)).join(''));
    return '<div class="ts-toolbar editor-only" role="toolbar" aria-label="Opciones de la sección">' + html + '</div>';
}

function marcadores(kind, titulo) {
    const nombre = escapeHtml(_plano(titulo) || 'Sección de tienda');
    const lock = getIcon('lock', 20);
    const uno = (estado, sufijo, texto) =>
        '<div class="ts-marker editor-only" data-ts-marker="' + estado + '">' +
            '<span class="ts-marker-ico" aria-hidden="true">' + lock + '</span>' +
            '<div><p class="ts-marker-t">' + nombre + ' · ' + sufijo + '</p>' +
            '<p class="ts-marker-x">' + texto + '</p></div>' +
        '</div>';
    return uno('oculto', 'oculta sin sesión', 'Quien no ha entrado en la tienda no ve esta sección.' + SOLO_EDITOR) +
        uno('vacio', 'sin contenido ahora', MARCADOR_VACIO[kind] + SOLO_EDITOR) +
        // `off`: el runtime pone el motivo (sin tienda, lista apagada en la tienda…).
        uno('off', 'no disponible', '<span data-ts-motivo>La tienda online no está activa.</span>' + SOLO_EDITOR);
}

function aviso(p, cfg, loginUrl) {
    const titulo = p.login_title == null ? t(cfg.tLoginTitle) : p.login_title;
    const texto = p.login_text == null ? t(cfg.tLoginText) : p.login_text;
    const cta = p.login_cta == null ? t('shop_login') : p.login_cta;
    return '<div class="ts-gate" data-ts-gate>' +
        '<span class="ts-gate-ico" aria-hidden="true">' + getIcon('lock', 24) + '</span>' +
        '<div class="ts-gate-text">' +
            '<h3 class="ts-gate-title" data-field="login_title" contenteditable="true">' + titulo + '</h3>' +
            '<p class="ts-gate-desc" data-field="login_text" contenteditable="true">' + texto + '</p>' +
        '</div>' +
        '<a class="ts-gate-btn" href="' + escapeHtml(loginUrl) + '" data-ts-login>' + getIcon('log-in', 18) +
            '<span data-field="login_cta" contenteditable="true">' + cta + '</span></a>' +
    '</div>';
}

function tiendaBlock(kind) {
    const cfg = TIENDA_TIPOS[kind];
    return (rawProps, index) => {
        const raw = rawProps || {};
        const p = safe(raw);
        const o = opcionesTienda(kind, raw);
        const titulo = raw.title == null ? t(cfg.tTitle) : p.title;
        const shop = getTiendaShop();
        const loginUrl = shop ? shop + '/login/' : '#';
        const params = {
            kind: kind, count: o.count, order: o.order, visibility: o.visibility, fallback: o.fallback,
            prices: o.prices, cart: o.cart, variant: o.variant, shop: shop, lang: getRenderLang(),
            tag: cfg.tTag ? t(cfg.tTag) : '', labels: etiquetas(),
        };
        const estado = o.visibility === 'todos' ? 'cargando' : 'pendiente';
        const tituloEditable = (clase) =>
            '<h2 class="' + clase + '" data-field="title" contenteditable="true">' + titulo + '</h2>';

        let cuerpo;
        if (o.variant === 'v3') {
            cuerpo =
                '<div class="ts-banner">' +
                    '<div class="ts-bmain">' +
                        '<div class="ts-bmedia" data-ts-slot="bfoto"><span class="ts-ph" aria-hidden="true"></span></div>' +
                        '<div class="ts-btext">' + tituloEditable('ts-kicker') +
                            '<div class="ts-bbody" data-ts-slot="bcuerpo"><span class="ts-skel-line"></span><span class="ts-skel-line ts-skel-short"></span></div>' +
                        '</div>' +
                    '</div>' +
                    '<div class="ts-brest" data-ts-slot="bresto"></div>' +
                '</div>';
        } else if (o.variant === 'v2') {
            cuerpo =
                '<div class="ts-head">' + tituloEditable('ts-title') +
                    '<div class="ts-arrows">' +
                        '<button type="button" class="ts-arrow" data-ts-dir="-1" aria-label="' + escapeHtml(t('shop_prev')) + '">' + getIcon('chevron-left', 18) + '</button>' +
                        '<button type="button" class="ts-arrow" data-ts-dir="1" aria-label="' + escapeHtml(t('shop_next')) + '">' + getIcon('chevron-right', 18) + '</button>' +
                    '</div>' +
                '</div>' +
                '<div class="ts-items ts-track" data-ts-slot="items" data-ts-track>' + esqueletos(kind, Math.min(o.count, 5)) + '</div>';
        } else {
            cuerpo = tituloEditable('ts-title ts-title-center') +
                '<div class="ts-items ts-grid" data-ts-slot="items">' + esqueletos(kind, Math.min(o.count, 4)) + '</div>';
        }

        return `
    <section id="bloque-${kind}-${index}" class="reveal variant-${o.variant} ts-section ts-${kind}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --accent-contrast:${p.accentContrast}; --font:'${p.fontFamily || "Inter"}'; --ts-card:${superficie(p.bgColor)}; padding:80px 8%;" data-tienda="${attrJson(params)}" data-ts-estado="${estado}">
        ${barra(kind, o)}
        <div class="ts-wrap">
            ${cuerpo}
            ${aviso(raw, cfg, loginUrl)}
            ${marcadores(kind, titulo)}
            <p class="ts-sample editor-only" data-ts-sample>Vista previa con los productos más vendidos: cada cliente verá los suyos.</p>
        </div>
    </section>`;
    };
}

export const ofertasBlock = tiendaBlock('ofertas');
export const novedadesBlock = tiendaBlock('novedades');
export const topBlock = tiendaBlock('top');
export const habitualBlock = tiendaBlock('habitual');
